import { CheckCircle2, MapPin, Phone, Truck } from "lucide-react"
import { format } from "date-fns"

import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"

interface FulfillmentDetailsProps {
  title?: string
  recipientName?: string
  phone?: string
  street?: string
  externalNumber?: string
  internalNumber?: string
  city?: string
  state?: string
  deliveredAt?: Date | null
  deliveredLabel?: string
  estimatedDeliveryLabel?: string
  showEstimatedDelivery?: boolean
  compact?: boolean
  className?: string
}

export function FulfillmentDetails({
  title,
  recipientName,
  phone,
  street,
  externalNumber,
  internalNumber,
  city,
  state,
  deliveredAt,
  deliveredLabel,
  estimatedDeliveryLabel,
  showEstimatedDelivery = false,
  compact = false,
  className,
}: FulfillmentDetailsProps) {
  if (compact) {
    return (
      <div
        className={cn(
          "flex items-start gap-3 text-xs font-bold uppercase tracking-widest text-muted-foreground",
          className
        )}
      >
        <MapPin className="h-3.5 w-3.5" />
        <div className="flex flex-col gap-1">
          {title && <span>{title}</span>}
          {(street || city || state) && (
            <span className="font-medium capitalize tracking-normal text-foreground">
              {street} {externalNumber}
              {city && `, ${city}`}
              {state && `, ${state}`}
            </span>
          )}
          <span className="flex flex-wrap items-center gap-4">
            {recipientName && <span>{recipientName}</span>}
            {phone && <span>{phone}</span>}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("flex flex-col gap-6 p-6", className)}>
      {title && (
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          {title}
        </span>
      )}
      <div className="space-y-5">
        <div className="flex items-start gap-4">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="space-y-1 text-sm">
            {recipientName && (
              <p className="font-bold uppercase tracking-wider text-foreground">{recipientName}</p>
            )}
            {phone && (
              <p className="flex items-center gap-2 font-medium text-muted-foreground">
                <Phone className="h-3 w-3" /> {phone}
              </p>
            )}
            <Separator className="my-2 bg-border/50" />
            <p className="font-medium capitalize text-muted-foreground">
              {street} {externalNumber} {internalNumber && `(${internalNumber})`}
            </p>
            {(city || state) && (
              <p className="text-xs font-bold uppercase tracking-widest text-foreground">
                {[city, state].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </div>

        {deliveredAt ? (
          <div className="flex items-center gap-2 text-xs font-bold text-primary">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {deliveredLabel ?? format(deliveredAt, "dd MMM yyyy")}
          </div>
        ) : (
          showEstimatedDelivery &&
          estimatedDeliveryLabel && (
            <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground">
              <Truck className="h-3.5 w-3.5" />
              {estimatedDeliveryLabel}
            </div>
          )
        )}
      </div>
    </div>
  )
}
